import { store } from 'createStore';
import type { SimulationResultInterface } from 'store/simulation/types';
import type { UnitInfoDataInterface } from 'store/unitInfoData';
import * as FeedConfig from 'unitData/Feed/FeedData';
import { roundToXDecimals } from 'util/conversions';
import type {
  PortOutputFlowInterface,
  UnitInfoBreakerInterface,
  UnitInfoFeederScreenInterface,
  UnitInfoFeedInterface,
  UnitInfoGrizzlyFeederInterface,
  UnitInfoPanFeederInterface,
  UnitInfoPlateFeederInterface,
  UnitInfoSplitInterface,
  UnitInterface,
} from '../../store/units/types';
import {
  feederScreenDeckConfig,
  feederScreenDeckNames,
  grizzlyFeederDeckConfig,
  grizzlyFeederDeckNames,
} from './deckDisplayConfigs';
import { renderDecksFromConfig } from './deckDisplayHelpers';
import { addNotesTexts } from './notesTexts';
import type { UnitInfoTextInterface } from './types';
import {
  addValue,
  attachmentTool,
  capacityRange,
  feederModelName,
  inclination,
  loadDisplay,
  modelName,
  toMetricCapacity,
  toMetricDensity,
  toMetricMM,
  unitName,
} from './utils';

export const getFeedAndScreenTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const unitInfo = unit.unitInfo as UnitInfoFeederScreenInterface;
  const load = simulationResult?.simulationUnitInfo?.load;

  unitName(unit.type, unitInfo, textArray);
  modelName(unitInfo, textArray);

  const nbrOfDecks = unitInfo.decks.length;

  renderDecksFromConfig(
    unitInfo.decks,
    nbrOfDecks,
    feederScreenDeckConfig.properties,
    textArray,
    feederScreenDeckNames,
  );

  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getPanFeederTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
): UnitInfoTextInterface[] => {
  const unitData = unitInfoData.PAN_FEEDER_SP;
  const textArray: UnitInfoTextInterface[] = [];
  const unitInfo = unit.unitInfo as UnitInfoPanFeederInterface;
  const { displayNominalCapacityRange } = unitInfo.display;
  const load = simulationResult?.simulationUnitInfo?.load;
  const autoSelectPanFeeder = simulationResult?.simulationUnitInfo?.modelName;

  unitName(unit.type, unitInfo, textArray);

  feederModelName(
    unitInfo,
    textArray,
    unitInfo.autoSelect &&
      autoSelectPanFeeder !== undefined &&
      autoSelectPanFeeder !== unitInfo.modelName
      ? `${unitData[autoSelectPanFeeder]?.descriptiveName}`
      : `${unitData[unitInfo.modelName]?.descriptiveName}`,
  );

  inclination(unitInfo, textArray);
  capacityRange(
    unitInfo.inclination < 8
      ? 0.88 * 0.3 * unitData[unitInfo.modelName]?.maxCapacity
      : unitInfo.inclination > 8
        ? 1.12 * 0.3 * unitData[unitInfo.modelName]?.maxCapacity
        : 0.3 * unitData[unitInfo.modelName]?.maxCapacity,
    unitInfo.inclination < 8
      ? 0.88 * unitData[unitInfo.modelName]?.maxCapacity
      : unitInfo.inclination > 8
        ? 1.12 * unitData[unitInfo.modelName]?.maxCapacity
        : unitData[unitInfo.modelName]?.maxCapacity,
    displayNominalCapacityRange,
    textArray,
  );

  loadDisplay(unitInfo, load, textArray);

  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getPlateFeederTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
): UnitInfoTextInterface[] => {
  const unitData = unitInfoData.PLATE_FEEDER_SH;
  const textArray: UnitInfoTextInterface[] = [];
  const unitInfo = unit.unitInfo as UnitInfoPlateFeederInterface;
  const { displayNominalCapacityRange } = unitInfo.display;
  const load = simulationResult?.simulationUnitInfo?.load;

  unitName(unit.type, unitInfo, textArray);
  feederModelName(unitInfo, textArray);
  capacityRange(
    0,
    unitData[unitInfo.modelName]?.maxCapacity,
    displayNominalCapacityRange,
    textArray,
  );
  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getSplitTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
  outputFlow?: PortOutputFlowInterface,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const unitInfo = unit.unitInfo as UnitInfoSplitInterface;
  const tphLeft = simulationResult?.simulationUnitInfo?.tphLeft;
  const tphRight = simulationResult?.simulationUnitInfo?.tphRight;
  const tphMiddle = simulationResult?.simulationUnitInfo?.tphMiddle;
  const { displaySplitSettings } = unitInfo.display;
  if (
    store.getState().settingsState?.unitDisplay?.showEquipmentType &&
    displaySplitSettings
  ) {
    textArray.push({ text: `${unitInfo.unitName}`, title: true });
  }

  const amount = (
    dir: 'left' | 'right' | 'middle',
    value: number | undefined,
  ) => {
    add(
      `Amount ${dir}: ${
        value !== undefined && outputFlow
          ? roundToXDecimals((value / outputFlow.totalMass) * 100, 0)
          : '-'
      } %`,
      displaySplitSettings,
    );
  };

  if (displaySplitSettings) {
    amount('left', tphLeft);
    amount('right', tphRight);
    if (unitInfo.nbrOfOutputs > 2) {
      amount('middle', tphMiddle);
    }
  }

  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getGrizzlyTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
) => {
  const textArray: UnitInfoTextInterface[] = [];
  const unitInfo = unit.unitInfo as UnitInfoGrizzlyFeederInterface;
  const load = simulationResult?.simulationUnitInfo?.load;

  unitName(unit.type, unitInfo, textArray);
  feederModelName(unitInfo, textArray);
  inclination(unitInfo, textArray);

  // Number of decks is nbrOfOutputs - 1 (first output is the grizzly output)
  const nbrOfDecks = Math.max(0, unitInfo.nbrOfOutputs - 1);

  renderDecksFromConfig(
    unitInfo.decks,
    nbrOfDecks,
    grizzlyFeederDeckConfig.properties,
    textArray,
    grizzlyFeederDeckNames,
  );

  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getFeedTexts = (unit: UnitInterface): UnitInfoTextInterface[] => {
  const unitInfo = unit.unitInfo as UnitInfoFeedInterface;
  const inputFlow = unitInfo.inputFlow ?? (unit as any).inputFlow;
  const textArray: UnitInfoTextInterface[] = [];

  if (!inputFlow) {
    console.warn('Feed unit missing inputFlow data', unit);
    return textArray;
  }

  const add = addValue(textArray);
  const {
    displayUnitName,
    displayMaterialName,
    displayFeedRate,
    displayPSDFeedCurveName,
    displayBulkDensity,
    displaySpecificGravity,
    displayWorkIndex,
    displayCompensatedWorkIndex,
    displayAbrasionIndex,
    displayMoisture,
    displayClay,
  } = unitInfo.display;

  const compensatedWorkIndex = FeedConfig.calcCompensatedWorkIndex(
    inputFlow.workIndex,
    inputFlow.specificGravity,
  );

  const displayCustomMaterialName =
    !!inputFlow.otherMaterialName && inputFlow.materialName === 'Custom';
  const feedRate = toMetricCapacity('Feed rate:', inputFlow.totalMass);
  const customPSD = inputFlow.psdName === 'Custom';

  add('Feed', displayUnitName, true);
  add(
    `${inputFlow.otherMaterialName ?? 'Custom'}`,
    displayMaterialName && displayCustomMaterialName,
  );
  add(
    inputFlow.materialName,
    displayMaterialName && !displayCustomMaterialName,
  );
  add(feedRate, displayFeedRate);
  add(
    `${inputFlow.otherPsdName ?? 'Custom'}`,
    displayPSDFeedCurveName && customPSD,
  );
  add(
    `${inputFlow.psdName} (<${toMetricMM('', inputFlow.maxStoneSize)})`,
    displayPSDFeedCurveName && !customPSD,
  );
  add(
    toMetricDensity('Bulk density:', inputFlow.bulkDensity),
    displayBulkDensity,
  );
  add(
    toMetricDensity('Specific gravity:', inputFlow.specificGravity),
    displaySpecificGravity,
  );
  add(`Work index: ${inputFlow.workIndex}`, displayWorkIndex);
  add(
    `Compensated work index: ${compensatedWorkIndex}`,
    displayCompensatedWorkIndex,
  );
  add(`Abrasion index: ${inputFlow.abrasionIndex}`, displayAbrasionIndex);
  add(`Moisture: ${inputFlow.moisture}% (by weight)`, displayMoisture);
  add(`Clay: ${inputFlow.clay}% (by weight)`, displayClay);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getBreakerTexts = (unit: UnitInterface) => {
  const textArray: UnitInfoTextInterface[] = [];
  const unitInfo = unit.unitInfo as UnitInfoBreakerInterface;
  addValue(textArray)('Attachment tool', true, true);
  attachmentTool(unit.type, unitInfo.attachmentTool, textArray);

  return textArray;
};
